import React, { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  FiCheck,
  FiChevronDown,
  FiFilter,
  FiSearch,
  FiShield,
  FiUsers,
  FiX,
} from "react-icons/fi";
import Spinner from "../components/Spinner";
import SchoolYearDropdown from "../components/SchoolYearDropdown";
import ErrorPage from "../components/ErrorPage";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { formatDateDMY, parseToDate } from "../utils/dateUtils";
import GenderIcon from "../components/GenderIcon";
import {
  PRIVACY_CHANNELS,
  PRIVACY_VISIBILITY_OPTIONS,
  DEFAULT_VISIBILITY,
} from "../features/privacy/definitions";
import {
  fetchCurrentSchoolYear,
  fetchSchoolYears,
} from "../utils/fetchSchoolYears";
import {
  normalizeClassroom,
  translateClassName,
} from "../utils/classroomUtils";


type EntityId = string | number;
type SelectValue = string;

interface SelectOption {
  value: SelectValue;
  label: string;
}

interface SchoolYear {
  id?: EntityId;
  name?: string;
  label?: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface ClassroomAssignment {
  id?: EntityId;
  uuid?: EntityId;
  classroomId?: EntityId;
  classroomUuid?: EntityId;
  classroomCode?: string;
  classroom?: {
    id?: EntityId;
    uuid?: EntityId;
  } | null;
  className?: string;
  name?: string;
  title?: string;
  schoolYearId?: EntityId | null;
  schoolYearLabel?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  current?: boolean;
  isCurrent?: boolean;
  [key: string]: unknown;
}

interface ChildRecord {
  id?: EntityId;
  uuid?: EntityId;
  childUuid?: EntityId;
  childUUID?: EntityId;
  firstName?: string | null;
  lastName?: string | null;
  gender?: string | null;
  waiting?: boolean;
  deceasedAt?: string | null;
  withdrawnAt?: string | null;
  classrooms?: ClassroomAssignment[];
  classroomAssignments?: ClassroomAssignment[];
  classroom_assignments?: ClassroomAssignment[];
  classroomAssignmentsDto?: ClassroomAssignment[];
  assignments?: ClassroomAssignment[];
  [key: string]: unknown;
}

interface PrivacyConsent {
  visibility?: string | null;
  updatedAt?: string | null;
  [key: string]: unknown;
}

interface CurrentClassroom {
  key: string;
  name: string;
}

interface PrivacyReportRow {
  child: ChildRecord;
  privacy: PrivacyConsent | null;
  currentClassroom?: CurrentClassroom | null;
}

interface BrowserSafeSelectProps {
  value: SelectValue;
  options: SelectOption[];
  onChange: (value: SelectValue) => void;
  ariaLabel: string;
  className?: string;
}


interface ConsentPillProps {
  value: boolean;
  yesLabel: string;
  noLabel: string;
}

interface VisibilityPillProps {
  visibility?: string | null;
  t: TFunction;
}

const getHttpStatus = (error: unknown): number | undefined => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status;
  }
  return undefined;
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message;
  return undefined;
};

const normalizeList = <T,>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (typeof data === "object" && data !== null && Array.isArray((data as { content?: T[] }).content)) return (data as { content: T[] }).content;
  if (typeof data === "object" && data !== null && Array.isArray((data as { items?: T[] }).items)) return (data as { items: T[] }).items;
  if (typeof data === "object" && data !== null && Array.isArray((data as { data?: T[] }).data)) return (data as { data: T[] }).data;
  return [];
};

const childIdentity = (child: ChildRecord | null | undefined): string | null => {
  const id = child?.uuid ?? child?.childUuid ?? child?.childUUID ?? child?.id;
  return id == null || id === "" ? null : String(id);
};

export const mergePrivacyReportChildren = (
  ...sources: ChildRecord[][]
): ChildRecord[] => {
  const childrenById = new Map<string, ChildRecord>();

  sources.forEach((source) => {
    source.forEach((child) => {
      const id = childIdentity(child);
      if (!id) return;
      const existing = childrenById.get(id);
      childrenById.set(id, existing ? { ...child, ...existing } : child);
    });
  });

  return Array.from(childrenById.values());
};

export const childrenFromClasslists = (data: unknown): ChildRecord[] => {
  const children = normalizeList<Record<string, unknown>>(data).reduce<ChildRecord[]>(
    (allChildren, classlist) => [
      ...allChildren,
      ...normalizeList<ChildRecord>(classlist.children),
    ],
    [],
  );
  return mergePrivacyReportChildren(children);
};

type SettledResult<T> =
  | { status: "fulfilled"; value: T }
  | { status: "rejected"; reason: unknown };

const settle = <T,>(promise: Promise<T>): Promise<SettledResult<T>> =>
  promise.then(
    (value) => ({ status: "fulfilled", value }),
    (reason) => ({ status: "rejected", reason }),
  );

export const buildFreshReportRequest = (
  requestVersion: number,
  params: Record<string, unknown> = {},
) => ({
  params: { ...params, _requestVersion: requestVersion },
  headers: {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
  },
});

const buildChildLabel = (child?: ChildRecord | null): string => {
  if (!child) return "";
  return [child.firstName, child.lastName].filter(Boolean).join(" ").trim();
};

const BrowserSafeSelect = ({
  value,
  options,
  onChange,
  ariaLabel,
  className = "",
}: BrowserSafeSelectProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0];

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-indigo-100/80 bg-white px-3 text-left text-sm font-semibold text-gray-900 shadow-sm transition hover:border-indigo-300 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-indigo-500/30 dark:bg-slate-950/80 dark:text-white dark:focus:ring-indigo-900/40"
      >
        <span className="min-w-0 truncate">{selected?.label || "—"}</span>
        <FiChevronDown
          aria-hidden="true"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 top-full z-[80] mt-1.5 max-h-64 min-w-full overflow-y-auto rounded-lg border border-indigo-100 bg-white p-1.5 shadow-2xl dark:border-indigo-500/30 dark:bg-slate-950"
        >
          {options.map((option) => {
            const active = String(option.value) === String(value);
            return (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-1.5 text-left text-sm transition ${
                  active
                    ? "bg-indigo-50 font-bold text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-200"
                    : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-slate-900"
                }`}
              >
                <span className="whitespace-nowrap">{option.label}</span>
                {active && <FiCheck aria-hidden="true" className="shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


const ConsentPill = ({ value, yesLabel, noLabel }: ConsentPillProps) => {
  const styles = value
    ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
    : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold leading-4 ${styles}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${value ? "bg-green-500" : "bg-red-500"}`} aria-hidden="true" />
      {value ? yesLabel : noLabel}
    </span>
  );
};

const DEFAULT_CHANNEL_LABELS = {};

const DEFAULT_VISIBILITY_LABELS = {
  VISIBLE: "Visible",
  NOT_VISIBLE: "Not visible",
  UNSET: "No preference set",
};

const formatClassName = (
  classroom: CurrentClassroom | ClassroomAssignment | string | null | undefined,
): string => {
  if (!classroom) return "";

  if (typeof classroom === "string") {
    const translated = translateClassName({ className: classroom });
    return translated || classroom;
  }

  const raw =
    classroom.name ||
    ("className" in classroom ? classroom.className : "") ||
    "";

  if (!raw) return "";

  const translated = translateClassName({ className: raw });
  return translated || raw;
};

const assignmentMatchesYear = (assignment: ClassroomAssignment | null | undefined, schoolYear: SchoolYear | null | undefined): boolean => {
  if (!assignment || !schoolYear) return false;
  const yearId = schoolYear?.id != null ? String(schoolYear.id) : null;
  const assignmentYearId = assignment.schoolYearId
    ? String(assignment.schoolYearId)
    : null;
  if (yearId && assignmentYearId && assignmentYearId === yearId) {
    return true;
  }

  const startYear = parseToDate(schoolYear.startDate)?.getFullYear();
  if (assignment.schoolYearLabel && startYear) {
    if (assignment.schoolYearLabel.includes(String(startYear))) {
      return true;
    }
  }

  const assignmentStart = parseToDate(assignment.startDate);
  const assignmentEnd = parseToDate(assignment.endDate);
  const yearStart = parseToDate(schoolYear.startDate);
  const yearEnd = parseToDate(schoolYear.endDate);

  if (assignmentStart && assignmentEnd && yearStart && yearEnd) {
    return assignmentStart <= yearEnd && assignmentEnd >= yearStart;
  }

  if (assignmentStart && yearEnd && !assignmentEnd) {
    return assignmentStart <= yearEnd;
  }

  if (assignmentEnd && yearStart && !assignmentStart) {
    return assignmentEnd >= yearStart;
  }

  return false;
};

const childInSchoolYear = (child: ChildRecord | null | undefined, schoolYear: SchoolYear | null | undefined): boolean => {
  if (!child || !schoolYear) return false;
  const rawAssignments =
    child.classrooms ??
    child.classroomAssignments ??
    child.classroom_assignments ??
    child.classroomAssignmentsDto ??
    child.assignments ??
    [];
  const assignments = Array.isArray(rawAssignments)
    ? rawAssignments.map((assignment) => {
        const normalized = normalizeClassroom(assignment);
        return {
          ...normalized,
          schoolYearId: normalized?.schoolYearId
            ? String(normalized.schoolYearId)
            : null,
        };
      })
    : [];
  if (!assignments.length) return true;

  return assignments.some((assignment) =>
    assignmentMatchesYear(assignment, schoolYear),
  );
};

export const selectPrivacyReportChildren = (
  children: ChildRecord[],
  schoolYear: SchoolYear | null,
  isYearScoped: boolean,
): ChildRecord[] => {
  if (!schoolYear || isYearScoped) return children;
  return children.filter((child) => childInSchoolYear(child, schoolYear));
};

const resolveCurrentClassroom = (child: ChildRecord | null | undefined, schoolYear: SchoolYear | null | undefined): CurrentClassroom | null => {
  if (!child) return null;
  const rawClassrooms =
    child.classrooms ??
    child.classroomAssignments ??
    child.classroom_assignments ??
    child.classroomAssignmentsDto ??
    child.assignments ??
    [];
  const classrooms = Array.isArray(rawClassrooms) ? rawClassrooms : [];
  if (!classrooms.length) return null;

  const normalized: Array<ClassroomAssignment & { __fallbackIndex: number }> =
    classrooms.map((assignment, index) => {
      const base = normalizeClassroom(assignment) as ClassroomAssignment;

      return {
        ...assignment,
        ...base,
        schoolYearId:
          base.schoolYearId != null ? String(base.schoolYearId) : null,
        __fallbackIndex: index,
      };
    });

  const filtered = schoolYear
    ? normalized.filter((cls) => assignmentMatchesYear(cls, schoolYear))
    : normalized;

  if (!filtered.length) return null;

  const byCurrentFlag = filtered.find(
    (cls) => cls?.current === true || cls?.isCurrent === true,
  );
  const withoutEndDate = filtered.find(
    (cls) => cls && (cls.endDate == null || cls.endDate === ""),
  );
  const fallback = filtered[0];
  const chosen = byCurrentFlag || withoutEndDate || fallback;
  if (!chosen) return null;

  const identifier =
    chosen.classroomId ??
    chosen.classroom?.id ??
    chosen.classroomUuid ??
    chosen.classroom?.uuid ??
    chosen.classroomCode ??
    chosen.className ??
    chosen.name ??
    chosen.id ??
    chosen.__fallbackIndex;

  const name = chosen.className || chosen.name || chosen.title || "";
  return { key: String(identifier), name: name || "" };
};

const resolveVisibilityLabel = (value: string | null | undefined, t: TFunction): string => {
  const normalized = value || DEFAULT_VISIBILITY;
  const option = PRIVACY_VISIBILITY_OPTIONS.find(
    ({ value: opt }) => opt === normalized,
  );
  const fallback = DEFAULT_VISIBILITY_LABELS[normalized] || normalized;
  if (!option?.labelKey) return fallback;
  return t(option.labelKey, { defaultValue: fallback });
};

const formatSchoolYearLabel = (year: SchoolYear | null | undefined): string => {
  if (!year) return "";
  const start = parseToDate(year.startDate);
  const end = parseToDate(year.endDate);
  if (start && end) return `${start.getFullYear()}-${end.getFullYear()}`;
  return year?.name || year?.label || String(year?.id ?? "");
};

const VisibilityPill = ({ visibility, t }: VisibilityPillProps) => {
  const normalized = visibility || DEFAULT_VISIBILITY;
  const label = resolveVisibilityLabel(visibility, t);
  const tone =
    normalized === "VISIBLE"
      ? { styles: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300", dot: "bg-green-500" }
      : normalized === "NOT_VISIBLE"
        ? { styles: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300", dot: "bg-red-500" }
        : { styles: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300", dot: "bg-slate-400" };

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold leading-4 ${tone.styles}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
      {label}
    </span>
  );
};

export default function ReportsPrivacy() {
  const { t, i18n } = useTranslation();
  const allowedLabel = t("reports.privacy_report.filter_yes", {
    defaultValue: "Allowed",
  });
  const notAllowedLabel = t("reports.privacy_report.filter_no", {
    defaultValue: "Not allowed",
  });
  const [rows, setRows] = useState<PrivacyReportRow[]>([]);
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [selectedSchoolYearId, setSelectedSchoolYearId] = useState<SelectValue>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [channelFilters, setChannelFilters] = useState<Record<string, SelectValue>>(() =>
    Object.fromEntries(PRIVACY_CHANNELS.map(({ key }) => [key, "any"])),
  );
  const [visibilityFilter, setVisibilityFilter] = useState<SelectValue>("any");
  const [classFilter, setClassFilter] = useState<SelectValue>("any");
  const [statusFilter, setStatusFilter] = useState<SelectValue>("any");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  const filtersCardRef = useRef<HTMLElement | null>(null);
  const tableHeadScrollRef = useRef<HTMLDivElement | null>(null);
  const [stickyTableHeadTop, setStickyTableHeadTop] = useState(0);
  const loadedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = `${i18n.language}|${selectedSchoolYearId ?? "__unset__"}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    let active = true;
    const requestVersion = Date.now();
    const freshRequest = (params: Record<string, unknown> = {}) =>
      buildFreshReportRequest(requestVersion, params);

    const fetchPrivacyForChild = async (childId: EntityId): Promise<PrivacyConsent | null> => {
      try {
        const { data } = await http.get(
          `/children/${childId}/privacy-consent`,
          freshRequest(),
        );
        return data || null;
      } catch (err: unknown) {
        const status = getHttpStatus(err);
        if (status === 404 || status === 204) {
          return null;
        }
        throw err;
      }
    };

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [privacyResponse, currentYearResponse, yearsResponse] =
          await Promise.all([
            settle(http.get("/reports/privacy-consents", freshRequest())),
            settle(fetchCurrentSchoolYear()),
            settle(fetchSchoolYears()),
          ]);

        if (!active) return;

        const currentYear =
          currentYearResponse.status === "fulfilled"
            ? currentYearResponse.value
            : null;
        const years =
          yearsResponse.status === "fulfilled" ? yearsResponse.value : [];
        setSchoolYears(Array.isArray(years) ? years : []);

        const currentSchoolYearId =
          currentYear?.id != null ? String(currentYear.id) : "";
        const fallbackSchoolYearId =
          currentSchoolYearId ||
          (Array.isArray(years) && years[0]?.id != null
            ? String(years[0].id)
            : "");
        const effectiveSelectedSchoolYearId =
          selectedSchoolYearId || fallbackSchoolYearId;

        if (!selectedSchoolYearId && fallbackSchoolYearId) {
          setSelectedSchoolYearId(fallbackSchoolYearId);
        }

        const fetchChildrenForYear = async (schoolYearId: SelectValue) => {
          if (!schoolYearId || schoolYearId === "all") {
            const res = await http.get("/children", freshRequest());
            return {
              children: normalizeList<ChildRecord>(res.data),
              isYearScoped: false,
            };
          }

          // These endpoints are complementary. In particular, `/children` can
          // return a valid but partial page, so a non-empty response must not
          // prevent the classlist data from contributing the remaining children.
          const [childrenResponse, classlistsResponse] = await Promise.all([
            settle(http.get("/children", freshRequest({ schoolYearId }))),
            settle(http.get("/reports/classlists", freshRequest({ schoolYearId }))),
          ]);

          const scopedChildren =
            childrenResponse.status === "fulfilled"
              ? normalizeList<ChildRecord>(childrenResponse.value.data)
              : [];
          const classlistChildren =
            classlistsResponse.status === "fulfilled"
              ? childrenFromClasslists(classlistsResponse.value.data)
              : [];
          const merged = mergePrivacyReportChildren(
            scopedChildren,
            classlistChildren,
          );
          if (merged.length > 0) {
            return { children: merged, isYearScoped: true };
          }

          const res = await http.get("/children", freshRequest());
          return {
            children: normalizeList<ChildRecord>(res.data),
            isYearScoped: false,
          };
        };

        const { children: childrenRaw, isYearScoped } = await fetchChildrenForYear(
          effectiveSelectedSchoolYearId,
        );

        // filter: niet overleden, niet uitgeschreven
        const activeChildren = childrenRaw.filter(
          (c) => !c.deceasedAt && !c.withdrawnAt,
        );

        const selectedYear =
          effectiveSelectedSchoolYearId !== "all"
            ? (Array.isArray(years) ? years : []).find(
                (year) =>
                  String(year?.id) === String(effectiveSelectedSchoolYearId),
              ) || null
            : null;

        let childrenInScope = selectPrivacyReportChildren(
          activeChildren,
          selectedYear,
          isYearScoped,
        );

        // If we default to current year and assignment metadata is missing, avoid an empty report.
        if (
          selectedYear &&
          currentYear &&
          String(selectedYear?.id) === String(currentYear?.id) &&
          activeChildren.length > 0 &&
          childrenInScope.length === 0
        ) {
          childrenInScope = activeChildren;
        }
        let combined: PrivacyReportRow[];

        let aggregateFailed = false;
        let aggregatedEntries: Array<Record<string, unknown>> = [];

        if (privacyResponse.status === "fulfilled") {
          aggregatedEntries = normalizeList<Record<string, unknown>>(privacyResponse.value?.data);
        } else {
          const status = getHttpStatus(privacyResponse.reason);
          if (status === 403) {
            aggregatedEntries = [];
          } else if (status === 404) {
            aggregateFailed = true;
          } else {
            throw privacyResponse.reason;
          }
        }

        if (!aggregateFailed) {
          const normalizeId = (value) =>
            value === undefined || value === null ? null : String(value);

          const privacyEntries: Array<[string, PrivacyConsent]> =
            aggregatedEntries.flatMap((entry) => {
              const nestedChild = entry.child as
                | { id?: EntityId; uuid?: EntityId }
                | undefined;

              const childId =
                normalizeId(entry.childId) ||
                normalizeId(entry.childUuid) ||
                normalizeId(nestedChild?.id) ||
                normalizeId(nestedChild?.uuid);

              if (!childId) return [];

              const payload = (entry.privacy ||
                entry.latestConsent ||
                entry) as PrivacyConsent;

              return [[childId, payload]];
            });

          const privacyByChildId = new Map<string, PrivacyConsent>(
            privacyEntries,
          );

          if (childrenInScope.length > 0) {
            combined = childrenInScope.map((child) => {
              const key =
                normalizeId(child.uuid) ||
                normalizeId(child.childUuid) ||
                normalizeId(child.childUUID) ||
                normalizeId(child.id);
              return {
                child,
                privacy: key ? (privacyByChildId.get(key) ?? null) : null,
              };
            });
          } else {
            // Fallback: show consent entries even if `/children` returns none (permissions or filtering mismatch).
            combined = Array.from(privacyByChildId.entries()).map(
              ([childId, payload]) => ({
                child: {
                  id: childId,
                  uuid: childId,
                  firstName: childId,
                  lastName: "",
                  classrooms: [],
                },
                privacy: payload ?? null,
              }),
            );
          }
        } else {
          const privacy = await Promise.all(
            childrenInScope.map((child) => fetchPrivacyForChild(child.id)),
          );

          combined = childrenInScope.map((child, index) => ({
            child,
            privacy: privacy[index],
          }));
        }

        const enriched = combined.map((entry) => ({
          ...entry,
          currentClassroom: resolveCurrentClassroom(entry.child, currentYear),
        }));

        setRows(enriched);
      } catch (err: unknown) {
        if (!active) return;
        const mapped = mapHttpErrorToMessage(err, t);
        setError(
          typeof mapped === "string"
            ? mapped
            : mapped?.message || mapped?.title || getErrorMessage(err),
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [i18n.language, selectedSchoolYearId]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter(({ child, privacy, currentClassroom }) => {
      if (child?.waiting) return false;
      const matchesSearch = !query
        ? true
        : buildChildLabel(child).toLowerCase().includes(query);

      if (!matchesSearch) return false;

      for (const { key } of PRIVACY_CHANNELS) {
        const filterValue = channelFilters[key] || "any";
        const consentValue = privacy?.[key];
        if (filterValue === "yes" && consentValue !== true) return false;
        if (filterValue === "no" && consentValue !== false) return false;
      }

      if (visibilityFilter !== "any") {
        const currentVisibility = privacy?.visibility ?? "UNSET";
        if (currentVisibility !== visibilityFilter) return false;
      }

      if (statusFilter === "completed" && !privacy) return false;
      if (statusFilter === "missing" && privacy) return false;

      if (classFilter !== "any") {
        if (classFilter === "__none__") {
          if (currentClassroom) return false;
        } else if (
          String(currentClassroom?.key ?? "") !== String(classFilter)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [rows, search, channelFilters, visibilityFilter, classFilter, statusFilter]);

  const classroomOptions = useMemo(() => {
    const map = new Map();
    const noneLabel = t("reports.privacy_report.filter_class_none");

    rows.forEach(({ currentClassroom }) => {
      if (currentClassroom && currentClassroom.name) {
        const label = formatClassName(currentClassroom);
        const value = currentClassroom.key
          ? String(currentClassroom.key)
          : label || "";
        const dedupKey = label ? label.toLowerCase() : value;
        if (!map.has(dedupKey)) {
          map.set(dedupKey || value, {
            value: value || dedupKey || "__unknown__",
            label: label || value,
          });
        }
      } else if (!map.has("__none__")) {
        map.set("__none__", { value: "__none__", label: noneLabel });
      }
    });

    if (!map.has("__none__")) {
      map.set("__none__", { value: "__none__", label: noneLabel });
    }

    const naturalClassSort = (a, b) => {
      if (a.value === "__none__") return -1;
      if (b.value === "__none__") return 1;

      const normalize = (label) => label.toLowerCase();
      const typeRank = (label) => {
        const lower = normalize(label);
        if (lower.includes("kindergarten") || lower.includes("kleuter"))
          return 0;
        if (
          lower.includes("grade") ||
          lower.includes("groep") ||
          lower.includes("klas")
        )
          return 1;
        return 2;
      };

      const numberFromLabel = (label) => {
        const match = label.match(/\d+/);
        return match ? parseInt(match[0], 10) : Number.NaN;
      };

      const rankA = typeRank(a.label);
      const rankB = typeRank(b.label);
      if (rankA !== rankB) return rankA - rankB;

      const numA = numberFromLabel(a.label);
      const numB = numberFromLabel(b.label);
      const numAValid = Number.isFinite(numA);
      const numBValid = Number.isFinite(numB);
      if (numAValid || numBValid) {
        if (!numAValid) return 1;
        if (!numBValid) return -1;
        if (numA !== numB) return numA - numB;
      }

      return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    };

    const sorted = Array.from(map.values()).sort(naturalClassSort);
    return sorted;
  }, [rows, t]);

  const schoolYearOptions = useMemo(() => {
    const years = Array.isArray(schoolYears) ? schoolYears : [];
    const sorted = years.slice().sort((a, b) => {
      const aStart = parseToDate(a?.startDate)?.getTime() ?? 0;
      const bStart = parseToDate(b?.startDate)?.getTime() ?? 0;
      return bStart - aStart;
    });

    return sorted
      .map((year) => {
        const value = year?.id != null ? String(year.id) : null;
        if (!value) return null;
        const label = formatSchoolYearLabel(year);
        return { value, label: label || value };
      })
      .filter(Boolean);
  }, [schoolYears]);

  const anyFilterLabel = t("reports.privacy_report.filter_any", {
    defaultValue: "Any",
  });
  const channelFilterOptions: SelectOption[] = [
    { value: "any", label: anyFilterLabel },
    { value: "yes", label: allowedLabel },
    { value: "no", label: notAllowedLabel },
  ];
  const visibilityFilterOptions: SelectOption[] = [
    { value: "any", label: anyFilterLabel },
    ...PRIVACY_VISIBILITY_OPTIONS.map(({ value, labelKey }) => ({
      value,
      label: t(labelKey, {
        defaultValue: DEFAULT_VISIBILITY_LABELS[value] || value,
      }),
    })),
  ];
  const classroomFilterOptions: SelectOption[] = [
    { value: "any", label: anyFilterLabel },
    ...classroomOptions,
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: "any", label: anyFilterLabel },
    { value: "completed", label: t("reports.privacy_report.summary_completed", { defaultValue: "Completed" }) },
    { value: "missing", label: t("reports.privacy_report.summary_missing", { defaultValue: "Missing" }) },
  ];

  const reportSummary = useMemo(() => {
    const withPrivacy = rows.filter(({ privacy }) => Boolean(privacy)).length;
    return {
      total: rows.length,
      visible: filteredRows.length,
      completed: withPrivacy,
      missing: Math.max(0, rows.length - withPrivacy),
    };
  }, [rows, filteredRows.length]);

  const clearAdvancedFilters = () => {
    setChannelFilters(
      Object.fromEntries(PRIVACY_CHANNELS.map(({ key }) => [key, "any"])),
    );
    setVisibilityFilter("any");
    setClassFilter("any");
    setStatusFilter("any");
  };

  const activeAdvancedFilterCount = useMemo(
    () =>
      [
        classFilter !== "any",
        visibilityFilter !== "any",
        statusFilter !== "any",
        ...Object.values(channelFilters).map((value) => value !== "any"),
      ].filter(Boolean).length,
    [classFilter, visibilityFilter, statusFilter, channelFilters],
  );

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!filtersRef.current?.contains(event.target as Node)) {
        setFiltersOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filtersOpen]);

  // De tabelkop plakt direct onder de sticky zoekkaart (top-3 = 12px + hoogte van de kaart).
  useEffect(() => {
    const card = filtersCardRef.current;
    if (!card) return undefined;
    const update = () => setStickyTableHeadTop(12 + card.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(card);
    return () => observer.disconnect();
  }, [loading, error]);

  const renderTableColumns = () => (
    <colgroup>
      <col className="w-[220px]" />
      <col className="w-[150px]" />
      {PRIVACY_CHANNELS.map(({ key }) => <col key={key} />)}
      <col />
      <col />
    </colgroup>
  );

  const renderTableHeaderRow = (screenReaderOnly: boolean) => {
    const headerLabel = (label: React.ReactNode) => (screenReaderOnly ? <span className="sr-only">{label}</span> : label);
    return (
      <tr className="bg-white dark:bg-slate-950">
        <th className="w-[220px] bg-white px-5 py-4 border-b border-slate-200 text-left text-xs font-bold uppercase tracking-[0.08em] text-slate-600 dark:bg-slate-950 dark:text-slate-400 dark:border-slate-800 lg:px-6">{headerLabel(t("reports.privacy_report.child_name"))}</th>
        <th className="w-[150px] bg-white px-4 py-4 border-b border-slate-200 text-left text-xs font-bold uppercase tracking-[0.08em] text-slate-600 dark:bg-slate-950 dark:text-slate-400 dark:border-slate-800">{headerLabel(t("reports.privacy_report.current_class"))}</th>
        {PRIVACY_CHANNELS.map(({ key, labelKey }) => <th key={key} className="bg-white px-4 py-4 border-b border-slate-200 text-left text-xs font-bold uppercase tracking-[0.08em] text-slate-600 dark:bg-slate-950 dark:text-slate-400 dark:border-slate-800">{headerLabel(t(labelKey, { defaultValue: DEFAULT_CHANNEL_LABELS[key] || key }))}</th>)}
        <th className="bg-white px-4 py-4 border-b border-slate-200 text-left text-xs font-bold uppercase tracking-[0.08em] text-slate-600 dark:bg-slate-950 dark:text-slate-400 dark:border-slate-800">{headerLabel(t("privacy.visibility.title", { defaultValue: "Visibility" }))}</th>
        <th className="bg-white px-4 py-4 border-b border-slate-200 text-left text-xs font-bold uppercase tracking-[0.08em] text-slate-600 dark:bg-slate-950 dark:text-slate-400 dark:border-slate-800">{headerLabel(t("privacy.last_updated"))}</th>
      </tr>
    );
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <ErrorPage
        message={error}
        status={500}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <>
      <style>{`
        @keyframes lds-report-enter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .lds-report-enter {
          animation: lds-report-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .privacy-report-table-shell {
          overflow-x: auto;
          border: 0 !important;
          border-radius: 0 !important;
        }

        /* Sticky kolomkoppen: aparte kop die horizontaal meescrollt met de tabel. */
        .privacy-report-table-head {
          overflow: hidden;
        }

        .privacy-report-table-head th {
          box-shadow: inset 0 -1px 0 rgb(226 232 240) !important;
        }

        @media (prefers-color-scheme: dark) {
          .privacy-report-table-head th {
            box-shadow: inset 0 -1px 0 rgb(30 41 59) !important;
          }
        }

        /* Kop in de datatabel blijft alleen voor screenreaders aanwezig. */
        .privacy-report-table thead.privacy-report-a11y-head th {
          height: 0 !important;
          padding: 0 !important;
          border: 0 !important;
          line-height: 0 !important;
        }

        .privacy-report-table {
          border: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          overflow: visible !important;
        }

        .privacy-report-table thead,
        .privacy-report-table thead tr,
        .privacy-report-table thead th {
          background: #fff !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }

        .privacy-report-table th:first-child,
        .privacy-report-table th:last-child {
          border-radius: 0 !important;
        }

        @media (prefers-color-scheme: dark) {
          .privacy-report-table thead,
          .privacy-report-table thead tr,
          .privacy-report-table thead th {
            background: rgb(2 6 23) !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .lds-report-enter {
            animation: none;
          }
        }
      `}</style>

      <main className="lds-report-enter px-4 py-5 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1600px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="space-y-6">
          <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-6 py-7 shadow-sm dark:border-indigo-900 dark:from-indigo-950/50 dark:via-slate-950 dark:to-sky-950/40 sm:px-8">
            <FiShield
              aria-hidden="true"
              className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[7rem] text-indigo-200 dark:text-indigo-900/40 sm:block"
            />

            <div className="relative z-10 flex min-w-0 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
                <FiShield aria-hidden="true" />
              </div>

              <div className="min-w-0 max-w-3xl">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300">
                  {t("reports.title", { defaultValue: "Reports" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
                  {t("reports.privacy.title", { defaultValue: "Privacy report" })}
                </h1>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  {t("reports.privacy_report.description", {
                    defaultValue:
                      "Review privacy preferences by child, class and school year.",
                  })}
                </p>
              </div>
            </div>
          </section>

          <div className="space-y-6">
        <section ref={filtersCardRef} className="sticky top-3 z-50 overflow-visible rounded-[24px] border border-slate-200/80 bg-white/95 shadow-lg backdrop-blur-md dark:border-slate-800 dark:bg-slate-950/95">
          <div className="flex flex-col gap-5 border-b border-slate-200/80 px-5 py-5 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between lg:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
                <FiShield className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-base font-bold text-slate-950 dark:text-white">
                  {t("reports.privacy_report.filters_title", {
                    defaultValue: "Search and filters",
                  })}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 dark:border-slate-800">
                  {t("reports.privacy_report.filters_description", {
                    defaultValue:
                      "Filter the report by child, school year, class and privacy preference.",
                  })}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {[
                [t("reports.privacy_report.summary_total", { defaultValue: "Children" }), reportSummary.total],
                [t("reports.privacy_report.summary_visible", { defaultValue: "Results" }), reportSummary.visible],
                [t("reports.privacy_report.summary_completed", { defaultValue: "Completed" }), reportSummary.completed],
                [t("reports.privacy_report.summary_missing", { defaultValue: "Missing" }), reportSummary.missing],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline gap-1.5">
                  <div className="text-sm font-bold text-slate-950 dark:text-white">{value}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 dark:border-slate-800">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-5 py-5 lg:px-6">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(250px,320px)_auto] md:items-end">
              <label className="min-w-0">

                <div className="relative">
                  <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("reports.privacy_report.search_placeholder", { defaultValue: "Search children" })}
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-10 text-sm font-semibold text-slate-950 outline-none transition placeholder:font-medium placeholder:text-slate-400 hover:border-indigo-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
                  />
                  {search && (
                    <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white" aria-label={t("reports.privacy_report.clear_search", { defaultValue: "Clear search" })}>
                      <FiX aria-hidden="true" />
                    </button>
                  )}
                </div>
              </label>

              <label className="min-w-0">

                <SchoolYearDropdown
                  schoolYears={schoolYears}
                  selectedSchoolYearId={selectedSchoolYearId}
                  onChange={setSelectedSchoolYearId}
                />
              </label>

              <div ref={filtersRef} className="relative md:justify-self-end">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((current) => !current)}
                  aria-haspopup="dialog"
                  aria-expanded={filtersOpen}
                  className={`inline-flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-bold shadow-sm transition dark:bg-slate-900 ${
                    filtersOpen || activeAdvancedFilterCount > 0
                      ? "border-indigo-300 text-indigo-700 ring-4 ring-indigo-100 dark:border-indigo-600 dark:text-indigo-300 dark:ring-indigo-950"
                      : "border-slate-200 text-slate-700 hover:border-indigo-300 hover:text-indigo-700 dark:border-slate-800 dark:text-slate-200"
                  }`}
                >
                  <FiFilter aria-hidden="true" />
                  {t("common.filters", { defaultValue: "Filters" })}
                  {activeAdvancedFilterCount > 0 && (
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[10px] font-bold text-white">
                      {activeAdvancedFilterCount}
                    </span>
                  )}
                </button>

                {filtersOpen && (
                  <div role="dialog" aria-label={t("common.filters", { defaultValue: "Filters" })} className="absolute right-0 top-full z-[90] mt-2 w-[min(360px,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-bold text-slate-950 dark:text-white">{t("common.filters", { defaultValue: "Filters" })}</h3>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 dark:border-slate-800">{t("reports.privacy_report.filters_description", { defaultValue: "Filter the report by class, privacy preference and status." })}</p>
                      </div>
                      <button type="button" onClick={() => setFiltersOpen(false)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-900 dark:hover:text-white" aria-label={t("common.close", { defaultValue: "Close" })}><FiX aria-hidden="true" /></button>
                    </div>

                    <div className="space-y-4">
                      <label className="block">
                        <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400 dark:border-slate-800">{t("reports.privacy_report.filter_class", { defaultValue: "Class" })}</span>
                        <BrowserSafeSelect value={classFilter} options={classroomFilterOptions} onChange={setClassFilter} ariaLabel={t("reports.privacy_report.filter_class", { defaultValue: "Class" })} className="[&>button]:h-11 [&>button]:rounded-xl" />
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400 dark:border-slate-800">{t("privacy.visibility.title", { defaultValue: "Privacy preference" })}</span>
                        <BrowserSafeSelect value={visibilityFilter} options={visibilityFilterOptions} onChange={setVisibilityFilter} ariaLabel={t("privacy.visibility.title", { defaultValue: "Privacy preference" })} className="[&>button]:h-11 [&>button]:rounded-xl" />
                      </label>
                      <label className="block">
                        <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400 dark:border-slate-800">{t("common.status", { defaultValue: "Status" })}</span>
                        <BrowserSafeSelect value={statusFilter} options={statusFilterOptions} onChange={setStatusFilter} ariaLabel={t("common.status", { defaultValue: "Status" })} className="[&>button]:h-11 [&>button]:rounded-xl" />
                      </label>
                    </div>

                    <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
                      <button type="button" onClick={clearAdvancedFilters} disabled={activeAdvancedFilterCount === 0} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl px-3 text-sm font-bold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-white"><FiX aria-hidden="true" />{t("common.clear", { defaultValue: "Clear filters" })}</button>
                      <button type="button" onClick={() => setFiltersOpen(false)} className="inline-flex h-10 items-center justify-center rounded-xl bg-indigo-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-950">{t("common.apply", { defaultValue: "Apply" })}</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-clip rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-center justify-between border-b border-slate-200/80 px-5 py-4 dark:border-slate-800 lg:px-6">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300"><FiUsers aria-hidden="true" /></div>
              <div>
                <h3 className="font-bold text-slate-950 dark:text-white">{t("reports.privacy_report.results", { defaultValue: "Privacy overview" })}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 dark:border-slate-800">{reportSummary.visible} {t("reports.privacy_report.results_count", { defaultValue: "results" })}</p>
              </div>
            </div>
          </div>

          <div
            ref={tableHeadScrollRef}
            className="privacy-report-table-head sticky z-40 bg-white dark:bg-slate-950"
            style={{ top: stickyTableHeadTop }}
            aria-hidden="true"
          >
            <table className="privacy-report-table min-w-[1480px] w-full table-fixed border-collapse text-sm">
              {renderTableColumns()}
              <thead className="bg-white dark:bg-slate-950">
                {renderTableHeaderRow(false)}
              </thead>
            </table>
          </div>

          <div
            className="privacy-report-table-shell"
            onScroll={(event) => {
              if (tableHeadScrollRef.current) tableHeadScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
            }}
          >
            <table className="privacy-report-table min-w-[1480px] w-full table-fixed border-collapse text-sm">
              {renderTableColumns()}
              <thead className="privacy-report-a11y-head">
                {renderTableHeaderRow(true)}
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredRows.length === 0 ? (
                  <tr><td colSpan={PRIVACY_CHANNELS.length + 4} className="px-5 py-10 text-center"><div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-900"><FiUsers className="h-5 w-5" /></div><p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{t("reports.privacy_report.no_results")}</p></td></tr>
                ) : filteredRows.map(({ child, privacy, currentClassroom }) => {
                  const name = buildChildLabel(child);
                  return (
                    <tr
                      key={String(child.id ?? child.uuid ?? name)}
                      className="bg-white transition hover:bg-indigo-50/40 dark:bg-slate-950 dark:hover:bg-indigo-950/20"
                    >
                      <td className="whitespace-nowrap px-5 py-4 font-bold text-slate-950 dark:text-white lg:px-6"><span className="inline-flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-900"><GenderIcon gender={child.gender} /></span><span>{name || "—"}</span></span></td>
                      <td className="whitespace-nowrap px-4 py-4 font-medium text-slate-600 dark:text-slate-300">{currentClassroom ? formatClassName(currentClassroom) : t("reports.privacy_report.filter_class_none")}</td>
                      {PRIVACY_CHANNELS.map(({ key }) => <td key={key} className="px-4 py-4">{privacy ? <ConsentPill value={privacy[key] === true} yesLabel={allowedLabel} noLabel={notAllowedLabel} /> : <span className="text-slate-400">—</span>}</td>)}
                      <td className="px-4 py-4">{privacy ? <VisibilityPill visibility={privacy.visibility} t={t} /> : <span className="text-slate-400">—</span>}</td>
                      <td className="whitespace-nowrap px-4 py-4 font-medium text-slate-500 dark:text-slate-400 dark:border-slate-800">{privacy ? (formatDateDMY(privacy.updatedAt) ?? "—") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
          </div>
          </div>
        </div>
      </main>
    </>
  );
}
